import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { cn } from "@/lib/cn";
import { dayStatusChipClass, dayStatusDotClass, dayStatusLabel } from "./utils";

export interface DayStatusControlProps {
  /** Day-start epoch-ms of the selected day. */
  readonly date: number;
  /** Current status from the dashboard payload (e.g. summary.dayStatus). */
  readonly status: string;
}

const DAY_STATUS_OPTIONS = ["not_logged", "partial", "complete", "fasting"] as const;

export function DayStatusControl({ date, status }: DayStatusControlProps) {
  const setDayStatus = useMutation(api.engine.setDayStatus).withOptimisticUpdate(
    (localStore, args) => {
      const queryArgs = { date: args.date, clientDayStart: args.date };
      const current = localStore.getQuery(api.dashboard.getByDate, queryArgs);
      if (current !== undefined) {
        localStore.setQuery(api.dashboard.getByDate, queryArgs, { ...current, dayStatus: args.status });
      }
    },
  );

  const handleSelect = (next: string) => {
    if (next === status) return;
    console.log("[Frontend][DayStatus] Setting day status", { date, from: status, to: next });
    setDayStatus({ date, clientDayStart: date, status: next as "complete" | "partial" | "not_logged" | "fasting" }).catch((err: unknown) => {
      console.warn("[Frontend][DayStatus] Failed to save day status", {
        date,
        status: next,
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return (
    <div
      className="rounded-2xl border border-border bg-surface px-4 py-3"
      data-testid="day-status-control"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Day status</p>
      <p className="mt-0.5 text-[11px] leading-tight text-text-muted">
        Tap how today went — complete and fasting days help your plan adapt.
      </p>
      <div className="mt-2 grid grid-cols-4 gap-1.5" role="group" aria-label="Day status options">
        {DAY_STATUS_OPTIONS.map((option) => {
          const active = option === status;
          return (
            <button
              key={option}
              type="button"
              onClick={() => handleSelect(option)}
              data-testid={`day-status-option-${option}`}
              aria-pressed={active}
              className={cn(
                "flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-xl px-1 py-1.5 text-[11px] font-semibold",
                "transition-all duration-200 ease-out active:scale-[0.97] select-none",
                active
                  ? cn(dayStatusChipClass(option), "ring-2 ring-black/15 shadow-sm")
                  : "bg-muted/60 text-text-muted active:bg-muted",
              )}
            >
              <span
                aria-hidden
                className={cn("inline-block h-2 w-2 rounded-full", dayStatusDotClass(option))}
              />
              {dayStatusLabel(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
